import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, ArrowLeft, Search, Filter, Plus, Heart,
  DollarSign, Utensils, Globe, ChevronRight, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecipeVault() {
  const { recipeId } = useParams();
  const { user } = useAuth();
  
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ region: 'all', health: 'all', cost: 'all' });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchRecipes();
  }, []);

  useEffect(() => {
    if (recipeId && recipes.length) {
      const recipe = recipes.find(r => r.id === recipeId);
      setSelectedRecipe(recipe);
    }
  }, [recipeId, recipes]);

  const fetchRecipes = async () => {
    try {
      const res = await fetch(`${API}/api/food/vault`);
      if (res.ok) {
        const data = await res.json();
        setRecipes(data.recipes || []);
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecipes = recipes.filter(recipe => {
    if (filter.region !== 'all' && recipe.region !== filter.region) return false;
    if (filter.health !== 'all' && !recipe.health_tags?.includes(filter.health)) return false;
    if (filter.cost !== 'all' && recipe.cost_tier !== filter.cost) return false;
    if (search && !recipe.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const regions = [...new Set(recipes.map(r => r.region))];
  const healthTags = [...new Set(recipes.flatMap(r => r.health_tags || []))];

  // Recipe Detail View
  if (selectedRecipe) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-orange-50/30" data-testid="recipe-detail">
        {/* Header */}
        <div className="bg-gradient-to-br from-orange-700 to-red-700 text-white py-12">
          <div className="container mx-auto px-4">
            <Link to="/food/vault" className="inline-flex items-center text-orange-200 hover:text-white mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Vault
            </Link>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge className="bg-white/20">{selectedRecipe.region}</Badge>
              <Badge className="bg-white/20">{selectedRecipe.cultural_origin}</Badge>
              <Badge className="bg-emerald-500">{selectedRecipe.cost_tier?.replace('_', ' ')}</Badge>
            </div>
            <h1 className="text-3xl font-bold mb-3">{selectedRecipe.title}</h1>
            <p className="text-orange-100 max-w-2xl">
              <Users className="inline h-4 w-4 mr-1" /> Contributed by {selectedRecipe.contributor}
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Family Story */}
              {selectedRecipe.family_story && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="py-6">
                    <div className="flex items-start gap-3">
                      <Heart className="h-6 w-6 text-amber-600 mt-1" />
                      <div>
                        <h3 className="font-bold text-amber-800 mb-2">The Story Behind This Recipe</h3>
                        <p className="text-amber-700 italic">"{selectedRecipe.family_story}"</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Ingredients */}
              <Card>
                <CardHeader>
                  <CardTitle>Ingredients</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedRecipe.ingredients?.map((ing, idx) => (
                      <div key={`item-${idx}`} className="flex items-start justify-between p-3 bg-stone-50 rounded-lg">
                        <div>
                          <span className="font-medium text-stone-800">{ing.name}</span>
                          <span className="text-stone-500 ml-2">{ing.quantity}</span>
                        </div>
                        {ing.local_substitute && (
                          <Badge variant="outline" className="text-xs">
                            Sub: {ing.local_substitute}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Instructions */}
              <Card>
                <CardHeader>
                  <CardTitle>Instructions</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    {selectedRecipe.instructions?.map((step, idx) => (
                      <li key={`item-${idx}`} className="flex gap-4">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <p className="text-stone-700 pt-1">{step}</p>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Legacy Swaps */}
              {selectedRecipe.legacy_swaps?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="h-5 w-5 text-rose-500" />
                      Health-Conscious Swaps
                    </CardTitle>
                    <CardDescription>Optional modifications for different dietary needs</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {selectedRecipe.legacy_swaps.map((swap, idx) => (
                      <div key={`item-${idx}`} className="p-4 bg-rose-50 rounded-lg mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-rose-100 text-rose-700">Optional</Badge>
                        </div>
                        <p className="text-stone-700">
                          Replace <strong>{swap.original}</strong> with <strong>{swap.swap}</strong>
                        </p>
                        <p className="text-sm text-stone-500 mt-1">Impact: {swap.impact}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Nutrition Card */}
              {selectedRecipe.nutritional_analysis && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Nutrition Facts</CardTitle>
                    <CardDescription>Per serving</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between border-b pb-2">
                        <span className="font-bold">Calories</span>
                        <span>{selectedRecipe.nutritional_analysis.calories_per_serving}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Protein</span>
                        <span>{selectedRecipe.nutritional_analysis.protein_g}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Carbohydrates</span>
                        <span>{selectedRecipe.nutritional_analysis.carbohydrates_g}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fat</span>
                        <span>{selectedRecipe.nutritional_analysis.fat_g}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fiber</span>
                        <span>{selectedRecipe.nutritional_analysis.fiber_g}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sodium</span>
                        <span>{selectedRecipe.nutritional_analysis.sodium_mg}mg</span>
                      </div>
                    </div>
                    
                    {selectedRecipe.nutritional_analysis.key_micronutrients && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium text-stone-700 mb-2">Key Nutrients</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedRecipe.nutritional_analysis.key_micronutrients.map(n => (
                            <Badge key={n} variant="outline" className="text-xs">{n}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Health Tags */}
              {selectedRecipe.health_tags?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Health Tags</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecipe.health_tags.map(tag => (
                        <Badge key={tag} className="bg-emerald-100 text-emerald-700">
                          {tag.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Share Recipe CTA */}
              <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white">
                <CardContent className="py-6 text-center">
                  <BookOpen className="h-10 w-10 mx-auto mb-3" />
                  <h3 className="font-bold mb-2">Share Your Heritage</h3>
                  <p className="text-orange-100 text-sm mb-4">
                    Submit your family recipe to the vault
                  </p>
                  <Link to="/food/vault/submit">
                    <Button className="bg-white text-orange-600 hover:bg-orange-50 w-full">
                      <Plus className="h-4 w-4 mr-2" /> Submit Recipe
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Recipe List View
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-orange-50/30" data-testid="recipe-vault">
      {/* Header */}
      <div className="bg-gradient-to-br from-amber-600 to-orange-600 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/food" className="inline-flex items-center text-amber-200 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to LegacyTable
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-8 w-8" />
            <h1 className="text-3xl font-bold">Global Recipe Vault</h1>
          </div>
          <p className="text-amber-100">Heritage recipes with AI nutritional analysis</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input 
              placeholder="Search recipes..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select 
            className="border rounded-md px-3 py-2 text-sm"
            value={filter.region}
            onChange={(e) => setFilter({...filter, region: e.target.value})}
          >
            <option value="all">All Regions</option>
            {regions.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select 
            className="border rounded-md px-3 py-2 text-sm"
            value={filter.cost}
            onChange={(e) => setFilter({...filter, cost: e.target.value})}
          >
            <option value="all">All Budgets</option>
            <option value="under_$5">Under $5</option>
            <option value="under_$10">Under $10</option>
            <option value="under_$20">Under $20</option>
          </select>
          <Link to="/food/vault/submit">
            <Button className="bg-orange-600 hover:bg-orange-700">
              <Plus className="h-4 w-4 mr-2" /> Submit Recipe
            </Button>
          </Link>
        </div>

        {/* Recipe Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecipes.map((recipe, idx) => (
            <motion.div
              key={recipe.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
            >
              <Card className="h-full hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{recipe.region}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <DollarSign className="h-3 w-3" />
                      {recipe.cost_tier?.replace('under_', '').replace('_', ' ')}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{recipe.title}</CardTitle>
                  <CardDescription>{recipe.cultural_origin}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-stone-600 mb-3 line-clamp-2">{recipe.family_story}</p>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {recipe.health_tags?.slice(0, 3).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                  
                  {recipe.nutritional_analysis && (
                    <div className="flex items-center gap-4 text-xs text-stone-500 mb-4">
                      <span>{recipe.nutritional_analysis.calories_per_serving} cal</span>
                      <span>{recipe.nutritional_analysis.protein_g}g protein</span>
                    </div>
                  )}
                  
                  <Link to={`/food/vault/${recipe.id}`}>
                    <Button variant="outline" className="w-full">
                      View Recipe <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {filteredRecipes.length === 0 && (
          <Card className="bg-stone-50">
            <CardContent className="py-12 text-center">
              <BookOpen className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No recipes found matching your filters.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
